// @flow

import * as React from 'react';
import {defineMessages, useIntl} from 'react-intl';
import classNames from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {AnimatePresence} from 'framer-motion';
import Box from 'sail/Box';
import {ContentSkeleton} from 'docs/components';
import {useArticle, useLocation, useAnalytics} from 'docs/data';
import {useContentFrame} from 'docs/shell/content/ContentFrameProvider';
import {scrollToHash} from 'docs/util';
import {MarkdocContent} from './MarkdocContent';
import {IntegrationBuilderContent} from './IntegrationBuilderContent';
import {VideoPlaylistContent} from './VideoPlaylistContent';
import {TailorComponentUIContent} from './TailorComponentUIContent';
import {TableOfContents} from './TableOfContents';
import {SearchIndexingMetadata} from './SearchIndexingMetadata';
import {Breadcrumbs} from './Breadcrumbs';
import './Content.scss';
import {StripeSessionsBanner} from './StripeSessionsBanner';
import {locationIsVSCode} from '../../util/locationIsVSCode';

const THRESHOLDS = [0, 25, 50, 75, 100];

const messages = defineMessages({
  stripeDocumentation: {
    id: 'docsShell.home.title',
    description: 'Title for the homepage of Docs',
    defaultMessage: 'Stripe Documentation',
  },
});

const getPageTitle = (article, intl) => {
  const stripeDocsTitle =
    intl?.formatMessage(messages.stripeDocumentation) || '';
  if (article?.path === '/docs') {
    return stripeDocsTitle;
  }
  const pageTitle = article?.metadata?.title || '';
  return pageTitle.length > 0 && stripeDocsTitle.length > 0
    ? `${pageTitle} | ${stripeDocsTitle}`
    : pageTitle;
};

export const Content = () => {
  const {article, isLoading} = useArticle();
  const {location, previousLocation} = useLocation();
  const {trackAction} = useAnalytics();

  const containerRef = useRef();
  const contentFrame = useContentFrame();
  const intl = useIntl();
  const [maxScrollPercentage, setMaxScrollPercentage] = useState(0);
  const prevMaxScrollPercentage = useRef(-1);

  // When a link is clicked to load a new article, the location object changes.
  // This causes the NavigationProvider to update the navigation (which causes
  // a rerender of the breadcrumbs) and the ArticleProvider to load a new article
  // (which sets isLoading to true). When this happens, there is a race condition
  // where isLoading is false but the location is updated (and thus the navigation and
  // breadcrumbs update). This would causes the breadcrumbs to flicker.
  // To avoid that flickering, we use this showSkeleton variable.
  let showSkeleton = isLoading || decodeURI(article.path) !== location.pathname;

  // When the VScode extension renders previews the pathname doesn't match
  // So we can't sync article with location when the origin is vscode
  if (locationIsVSCode(location)) {
    showSkeleton = false;
  }

  // Ensure the content frame is focused when the page is initially loaded
  // and set the contentFrame container reference so components can spy
  // scroll position.
  useEffect(() => {
    if (containerRef.current && !locationIsVSCode(location)) {
      containerRef.current.focus();
    }
    contentFrame.setContainerRef(containerRef);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [containerRef]);

  // When the hash changes, attempt to scroll to the associated anchor. If one doesn't
  // exist, scroll to the top instead. This behavior can be skipped by passing a false
  // value for the "scroll" option when navigating.
  useEffect(() => {
    if (location.state?.scroll !== false) {
      if (!scrollToHash(location.hash)) {
        contentFrame.setScrollPosition(0);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, [isLoading, location.hash]);

  useEffect(() => {
    if (location.state?.scroll !== false) {
      if (previousLocation.toString() === location.toString()) {
        scrollToHash(location.hash);
      }
    }
    // This effect tracks the current pathname and when users navigate to other pages the counters reset
    if (previousLocation.pathname.toString() !== location.pathname.toString()) {
      // reset progress trackers
      setMaxScrollPercentage(0);
      prevMaxScrollPercentage.current = -1;
    }
    /**
     * `location` is passed here instead of `location.hash` so the scroll behavior
     * fires on subsequent clicks.
     */
  }, [location, previousLocation]);

  // When a user scrolls we determine the percentage of the page they have scrolled
  // and keep track of the maximum percentage of the page that's been scrolled.
  useEffect(() => {
    return contentFrame.onScroll(() => {
      const scrollableHeight = contentFrame.getScrollableContentHeight();
      const curScrollPosition = contentFrame.getScrollPosition();
      // We use a curScrollPercentage of 0 on pages that have a scrollableHeight of 0
      const curScrollPercentage = scrollableHeight
        ? Math.round((curScrollPosition / scrollableHeight) * 100)
        : 0;
      setMaxScrollPercentage(() => {
        if (curScrollPercentage > maxScrollPercentage) {
          return curScrollPercentage;
        }
        return maxScrollPercentage;
      });
    });
  }, [contentFrame, maxScrollPercentage]);

  // This effect triggers analytics events when the scrolled position passes 0%, 25%, 50%, 75%, 100%
  // These ranges are used instead of equating to an increment (e.g. === 25) because a race condition
  // exists where the maxScrollPosition can change before the check.
  useEffect(() => {
    for (const scrollPercentage of THRESHOLDS) {
      if (
        maxScrollPercentage >= scrollPercentage &&
        prevMaxScrollPercentage.current < scrollPercentage
      ) {
        trackAction('scroll_progress', {
          scrollPercentage,
        });
        break;
      }
    }
    prevMaxScrollPercentage.current = maxScrollPercentage;
  }, [trackAction, maxScrollPercentage]);

  let content;
  let tableOfContents;

  const showFullPageContent = [
    'integration-builder',
    'video-playlist',
  ].includes(article.metadata?.type);
  const notResponsive = article.metadata?.type === 'integration-builder';
  const showBreadcrumbs = !showSkeleton && !showFullPageContent;

  if (article.metadata?.type === 'integration-builder') {
    content = <IntegrationBuilderContent article={article} />;
  } else if (article.metadata?.type === 'sail-next-component-ui') {
    content = <TailorComponentUIContent article={article} />;
    tableOfContents = (
      <TableOfContents article={article} contentRef={containerRef} />
    );
  } else if (article.metadata?.type === 'video-playlist') {
    content = <VideoPlaylistContent article={article} />;
  } else if (showSkeleton) {
    content = <ContentSkeleton />;
  } else {
    content = <MarkdocContent article={article} />;
    tableOfContents = (
      <TableOfContents article={article} contentRef={containerRef} />
    );
  }

  return (
    <Box
      boxRef={containerRef}
      className={classNames(
        'Content',
        showFullPageContent && 'Content-fullPage',
        notResponsive && 'Content-fixedWidthFullPage',
      )}
      tabIndex="0"
      data-type={article.metadata.type}
    >
      <Box className="Content-container">
        <Helmet>
          <title>{getPageTitle(article, intl)}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Helmet>
        <SearchIndexingMetadata article={article} />
        <Box
          className="Content-articleContainer"
          background="white"
          padding={{bottom: 32, horizontal: 48}}
        >
          {/* TODO(matv, 2022-06-01): delete StripeSessionsBanner */}
          <StripeSessionsBanner />
          {showBreadcrumbs && <Breadcrumbs />}
          <Box flex={{direction: 'row'}}>
            <AnimatePresence initial={false}>{content}</AnimatePresence>
            {tableOfContents}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
